export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatCompletionResponse {
  content: string;
  error?: string;
}

export class OpenAIService {
  private apiKey: string | null = null;
  private baseURL = 'https://api.openai.com/v1';

  constructor() {
    // Load API key from localStorage
    this.apiKey = localStorage.getItem('openai_api_key');
  }

  setApiKey(key: string): void {
    this.apiKey = key;
    localStorage.setItem('openai_api_key', key);
  }

  getApiKey(): string | null {
    return this.apiKey;
  }

  hasApiKey(): boolean {
    return !!this.apiKey;
  }

  clearApiKey(): void {
    this.apiKey = null;
    localStorage.removeItem('openai_api_key');
  }

  async chatCompletion(
    messages: ChatMessage[],
    options?: {
      model?: string;
      temperature?: number;
      maxTokens?: number;
    }
  ): Promise<ChatCompletionResponse> {
    if (!this.apiKey) {
      return {
        content: '',
        error: 'OpenAI API key not configured. Please add your API key in Settings.',
      };
    }

    try {
      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: options?.model || 'gpt-4o-mini',
          messages,
          temperature: options?.temperature || 0.7,
          max_tokens: options?.maxTokens || 2000,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        return {
          content: '',
          error: error.error?.message || 'Failed to get response from OpenAI',
        };
      }

      const data = await response.json();
      return {
        content: data.choices[0]?.message?.content || '',
      };
    } catch (error) {
      return {
        content: '',
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }
}

export const openAIService = new OpenAIService();
